import { useMutation, useQueryClient } from "@tanstack/react-query";
import { apiFetch, ApiError } from "../api/client";
import { useEvent } from "../context/EventProvider";
import { liveKey, liveScope } from "./channel";
import { logAction } from "../log/actionLog";
import { liveLog } from "../log/labels";
import type { Activity, LivePayload } from "./types";

/**
 * RÉPONDRE.
 *
 * ⚠️ AUCUNE RÈGLE N'EST APPLIQUÉE ICI, ET C'EST VOULU. Combien de choix, lesquels,
 * la phase, le droit de vote : tout est vérifié par le serveur, parce que rien de
 * ce qu'un écran empêche n'est empêché. L'écran désactive le bouton pour être
 * agréable ; le serveur refuse pour être juste.
 *
 * ⚠️ ET ON RELIT APRÈS. La diffusion ne porte pas `myAnswers` (elle est la même
 * pour tout le monde), donc c'est la relecture qui apprend à CET écran qu'il a
 * répondu. Sans elle, un rechargement reproposerait les boutons.
 *
 * ⚠️ MAIS L'ÉCRAN NE L'ATTEND PAS POUR LE DIRE. Mesuré : 2,6 secondes entre le clic
 * et le moindre changement, parce que la relecture complète du direct devait revenir
 * avant que « votre réponse est enregistrée » remplace les boutons. Pendant ce temps,
 * un rouage tourne dans le bouton et rien d'autre ne bouge — signalé tel quel :
 * « quand on répond il ne se passe rien visuellement ». Deux secondes et demie après
 * un vote, dans une salle, c'est le temps qu'il faut pour recliquer.
 *
 * On écrit donc la réponse dans le cache DÈS QUE LE SERVEUR A DIT OUI, et la
 * relecture qui suit remplace ce provisoire par la vérité. Ce n'est pas de
 * l'optimisme : le 200 EST la confirmation, on cesse seulement de l'ignorer jusqu'au
 * prochain aller-retour.
 */
export function useAnswer(activity: Activity | null) {
  const event = useEvent();
  const activityId = activity?.id ?? null;
  const client = useQueryClient();

  return useMutation({
    mutationFn: async (body: { choiceIds?: number[]; text?: string }) => {
      if (!event || !activityId) throw new Error("Aucune activité en cours");
      return apiFetch<{ answered: number }>(
        `/domains/${event.domainId}/events/${event.eventId}/live/activities/${activityId}/answers`,
        { api: "apix", method: "POST", body },
      );
    },
    onSuccess: (_answer, submitted) => {
      if (!event) return;

      /* Au journal de la fiche contact, une ligne par réponse enregistrée. Une lecture
         qui lèverait ici ferait passer la réponse acceptée pour un refus. */
      try {
        const entry = activity && liveLog(activity, submitted);
        if (entry) logAction(event, entry.action, { label: entry.label });
      } catch {
        /* le journal se tait */
      }

      /* La charge du PARTICIPANT : sans jeton d'écran, donc `liveKey(d, e)`. Un écran
         de salle ne répond jamais, la régie non plus. */
      client.setQueryData<LivePayload>(
        liveKey(event.domainId, event.eventId),
        (current) =>
          current
            ? {
                ...current,
                myAnswers: [
                  ...(current.myAnswers ?? []),
                  ...pendingAnswers(submitted),
                ],
              }
            : current,
      );

      /* La PORTÉE, pas la clé : depuis que le jeton d'écran en fait partie, viser
         la clé exacte laisserait les autres demandeurs sur une charge périmée. */
      void client.invalidateQueries({
        queryKey: liveScope(event.domainId, event.eventId),
      });
    },
  });
}

/**
 * CE QU'ON VIENT D'ENVOYER, en attendant que le serveur le renvoie.
 *
 * ⚠️ L'IDENTIFIANT EST NÉGATIF, ET C'EST DÉLIBÉRÉ. Le serveur ne rend pas les
 * identifiants créés, et rien ne les lit ici (`answeredChoices` ne regarde que
 * `choiceId`, `hasAnswered` que le nombre) : un nombre négatif dit « ceci n'existe pas
 * encore côté serveur » à qui lirait ce cache, là où un `0` ressemble à une vraie
 * clé. La relecture qui suit remplace toute la liste.
 */
export function pendingAnswers(submitted: { choiceIds?: number[]; text?: string }) {
  if (submitted.choiceIds?.length) {
    return submitted.choiceIds.map((choiceId) => ({
      id: -choiceId,
      choiceId,
      text: null,
    }));
  }
  if (submitted.text) return [{ id: -1, choiceId: null, text: submitted.text }];
  return [];
}

/**
 * CE QUE J'AI DÉJÀ RÉPONDU, sur l'activité en cours.
 *
 * ⚠️ LA QUESTION N'EST PAS « AI-JE CLIQUÉ », c'est « le serveur a-t-il enregistré ».
 * La V1 garde la réponse dans l'état React seul : on recharge la page au milieu
 * d'un vote et les boutons reviennent, pour se faire refuser une seconde plus tard.
 */
export function answeredChoices(live: LivePayload | undefined): number[] {
  return (live?.myAnswers ?? [])
    .map((answer) => answer.choiceId)
    .filter((id): id is number => typeof id === "number");
}

export function hasAnswered(live: LivePayload | undefined): boolean {
  return (live?.myAnswers ?? []).length > 0;
}

/** Le message du serveur, qui dit POURQUOI, plutôt qu'un « une erreur est survenue ». */
export function refusalOf(error: unknown): string {
  if (error instanceof ApiError) return error.message;
  if (error instanceof Error) return error.message;
  return "Votre réponse n'a pas pu être envoyée.";
}
